import { Injectable } from '@angular/core';
import { Observable, ReplaySubject, tap, of } from 'rxjs';
import { Navigation } from 'app/core/navigation/navigation.types';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { UserOrganizationRole } from 'app/models/organization/organization';

const dashboard: FuseNavigationItem = {
  icon: "mat_outline:dashboard",
  id: "admin.dashboard",
  link: "/dashboard",
  title: "Dashboard",
  type: "basic",
  classes: {
    wrapper: 'pw-menu-item-dashboard-organization',
  }
}
const workSessions: FuseNavigationItem = {
  id: "track",
  type: "group",
  title: "Track",
  children: [{
    icon: "mat_outline:access_time",
    id: "work-historyid",
    link: "/user/work-history",
    title: "Work Sessions",
    type: "basic",
    meta: 'data-testid',
    classes: {
      wrapper: 'pw-menu-item-work-sessions',
    }
    }
  ]
}

const tickets: FuseNavigationItem = {
  icon: "playlist_add_check",
  id: "tasks",
  link: "/tasks",
  title: "Tasks",
  type: "basic",
  classes: {
    wrapper: 'pw-menu-item-tasks',
  }
}

const analyze: FuseNavigationItem = {
  id: "analyze",
  type: "group",
  title: "Analyze",
  children: [
    {
      icon: "mat_outline:dashboard",
      id: "admin.dashboard",
      link: "/dashboard/member",
      title: "Dashboard",
      type: "basic",
      classes: {
        wrapper: 'pw-menu-item-dashboard-member',
      }
    },
    {
      icon: "mat_outline:collections",
      id: "reports",
      title: "Reports",
      type: "collapsable",
      exactMatch: true,
      children: [
        {
          icon: "heroicons_solid:table",
          id: "time-reports",
          link: "/user/time-reports",
          title: "Time reports",
          type: "basic",
          classes: {
            wrapper: 'pw-menu-item-time-reports-member',
          }
        }
      ]
    }
  ]
}

const project: FuseNavigationItem = {
  icon: "heroicons_outline:briefcase",
  id: "projects",
  link: "projects",
  title: "Projects",
  type: "basic",
  exactMatch: true,
  classes: {
    wrapper: 'pw-menu-item-projects',
  }
}

const integrations: FuseNavigationItem = {
  icon: "plug-connect",
  id: "integrations",
  title: "Integrations",
  type: "basic",
  link: "integrations",
  classes: {
    wrapper: 'pw-menu-item-integrations',
  }
};

const people: FuseNavigationItem = {
  icon: "mat_solid:people_outline",
  id: "membersandteams",
  link: "members",
  title: "Members and Teams",
  type: "basic",
  classes: {
    wrapper: 'pw-menu-item-membersandteams',
  }
}

const navigationList: Navigation = {
  compact: [],
  futuristic: [],
  horizontal: [],
  default: [],
  footer: [
    {
      id: "admin ",
      type: "group",
      title: "Admin",
    },
    {
      icon: "mat_outline:settings",
      id: "settings",
      title: "Settings",
      type: "basic",
      link: "/settings",
      exactMatch: true,
      classes: {
        wrapper: 'pw-menu-item-settings',
      }
    }
  ]
};


@Injectable({
  providedIn: 'root'
})
export class NavigationService {
  private _navigation: ReplaySubject<Navigation> = new ReplaySubject<Navigation>(1);
  get navigation$(): Observable<Navigation> {
    return this._navigation.asObservable();
  }
  private getManageMenu(role: UserOrganizationRole) {
    const manage: FuseNavigationItem = {
      id: "manage",
      type: "group",
      title: "Manage",
      children: []
    };
    switch (role) {
      case UserOrganizationRole.Owner:
        return {
          ...manage, children: [
            {
              ...tickets
            },
            {
              ...project
            },
            {
              ...people
            },
            {
              ...integrations
            }
          ]
        }
      case UserOrganizationRole.Member:
      case UserOrganizationRole.Viewer:
        return {
          ...manage, children: [
            {
              ...tickets
            },
          ]
        }
    }
  }

  // private getTrackMenu(isScreenshots = true): FuseNavigationItem {
  //   if (isScreenshots) {
  //     return {
  //       id: "track",
  //       type: "group",
  //       title: "Track",
  //       children: [
  //         {
  //           icon: "mat_outline:history",
  //           id: "work-history",
  //           title: "Work History",
  //           type: "collapsable",
  //           exactMatch: true,
  //           children: [
  //             {
  //               ...workSessions
  //             },
  //             {
  //               icon: "heroicons_outline:camera",
  //               id: "screenshots",
  //               link: "/user/work-history/screenshots",
  //               title: "Screenshots",
  //               type: "basic",
  //               classes: {
  //                 wrapper: 'pw-menu-item-screenshots-member',
  //               }
  //             },
  //           ]
  //         }
  //       ]
  //     };
  //   } else return {
  //     ...workSessions
  //   }
  // }
  private getAnalizeMenu(isScreenshots = true): FuseNavigationItem {
    if (isScreenshots) {
      return {
        id: "analyze",
        type: "group",
        title: "Analyze",
        children: [
          { ...dashboard },
          {
            icon: "stack-star",
            id: "reports",
            title: "Reports",
            type: "collapsable",
            children: [
              {
                icon: "heroicons_solid:table",
                id: "time-reports",
                link: "/reports/time/weekly",
                title: "Time reports",
                type: "basic",
                classes: {
                  wrapper: 'pw-menu-item-time-reports-organization',
                }
              },
              {
                icon: "heroicons_outline:camera",
                id: "screenshots",
                link: "/reports/screenshots",
                title: "Screenshots",
                type: "basic",
                classes: {
                  wrapper: 'pw-menu-item-screenshots-organization',
                }
              },
            ]
          }
        ]
      };
    } else return {
      id: "analyze",
      type: "group",
      title: "Analyze",
      children: [
        { ...dashboard },
        {
          icon: "heroicons_solid:table",
          id: "time-reports",
          link: "/reports/time/weekly",
          title: "Time reports",
          type: "basic",
          classes: {
            wrapper: 'pw-menu-item-time-reports-organization',
          }
        },
      ]
    };
  }

  get(role: UserOrganizationRole): Observable<Navigation> {
    switch (role) {
      case UserOrganizationRole.Owner:
        navigationList.default = [workSessions, this.getAnalizeMenu(), this.getManageMenu(role)];
        break;
      case UserOrganizationRole.Member:
      case UserOrganizationRole.Viewer:
        navigationList.default = [workSessions, analyze, this.getManageMenu(role)];
        break;
    }

    return of(navigationList).pipe(
      tap((navigation) => {
        this._navigation.next(navigation);
      }),
    );
  }
}